import type { AbilityRef, UnitDef } from "../schema/unit.js";
import type { Chassis, Manufacturer, Role, Tier } from "../schema/common.js";
import { ARCHETYPE_NAMES } from "./archetypes.js";
import { PROTOCOLS, type Protocol } from "../schema/ability.js";

type P = Record<string, number>;

function a(archetype: string, params: P = {}, name?: [string, string]): AbilityRef {
  const n = name ?? ARCHETYPE_NAMES[archetype] ?? [archetype, archetype];
  return { archetype, name: { de: n[0], en: n[1] }, params };
}

const rule = (p: Protocol): number => PROTOCOLS.indexOf(p);
const aura = (stat: "atk" | "def" | "hp" | "crit" | "energy", pct: number): AbilityRef =>
  a("abl.aura", { stat: ["atk", "def", "hp", "crit", "energy"].indexOf(stat), pct }, [
    `Aura: ${stat.toUpperCase()}`,
    `Aura: ${stat.toUpperCase()}`,
  ]);

function unit(
  id: string,
  name: [string, string],
  mfr: Manufacturer,
  chassis: Chassis,
  role: Role,
  tier: Tier,
  standard: AbilityRef,
  uniques: AbilityRef[],
  bio: [string, string],
): UnitDef {
  const def: UnitDef = { id, name: { de: name[0], en: name[1] }, mfr, chassis, role, tier, standard, uniques, bio: { de: bio[0], en: bio[1] } };
  return role === "commander" ? { ...def, commandFrame: COMMAND_FRAMES[id] ?? 100 } : def;
}

/**
 * How much of the front's combined health a commander's own frame carries in battle (R38, D76): a heavy shell
 * stands far longer than a scout once the front is gone.
 */
const COMMAND_FRAMES: Record<string, number> = {
  "unit.aegis_prime": 130,
  "unit.warden_kastor": 125,
  "unit.ironclad_boone": 120,
  "unit.sgt_rook": 115,
  "unit.sable_marrow": 115,
  "unit.overwatch_nyx": 110,
  "unit.halcyon_vex": 110,
  "unit.cipher_odd": 105,
  "unit.cadet_01": 100,
};

/** The 45 launch units (30-units.md R24). */
export const UNITS: readonly UnitDef[] = [
  // ─── Commanders ───
  unit("unit.aegis_prime", ["Aegis Prime", "Aegis Prime"], "HEX", "tank", "commander", "S",
    a("abl.command_pulse", { mult: 1.0, rule: rule("lowest_hp") }, ["Kommandokanone", "Command Cannon"]),
    [a("abl.pulse_rally", { pct: 20, ticks: 35 }), a("abl.pulse_fortify", { pct: 20, ticks: 35 }), a("abl.last_stand", { pct: 25 }), a("abl.ultimate_orbital", { mult: 2.6 })],
    ["Der letzte Feldmarschall von Hexa Dynamics, in einer Hülle aus Wolframstahl. Er zählt Verluste in Sekunden, nicht in Einheiten.", "Hexa Dynamics' last field marshal in a tungsten shell. He counts losses in seconds, never in units."]),
  unit("unit.overwatch_nyx", ["Overwatch Nyx", "Overwatch Nyx"], "VLK", "tripod", "commander", "S",
    a("abl.command_pulse", { mult: 0.9, rule: rule("highest_atk") }, ["Lanzenimpuls", "Lance Pulse"]),
    [a("abl.pulse_focus", { pct: 22, ticks: 35 }), a("abl.pulse_overdrive", { pct: 20, ticks: 30 }), a("abl.pulse_inspire", { amount: 25 }), a("abl.ultimate_lockdown", { ticks: 20 })],
    ["Ein Valkyr-Zielrechner mit einem Namen und einer Meinung. Nyx sieht den gefährlichsten Gegner zuerst und lässt ihn nicht mehr los.", "A Valkyr targeting core with a name and an opinion. Nyx sees the most dangerous enemy first and never lets go."]),
  unit("unit.warden_kastor", ["Wächter Kastor", "Warden Kastor"], "HEX", "tank", "commander", "A",
    a("abl.command_pulse", { mult: 0.9, rule: rule("defender_first") }, ["Belagerungsimpuls", "Siege Pulse"]),
    [a("abl.pulse_fortify", { pct: 18, ticks: 30 }), a("abl.pulse_rally", { pct: 15, ticks: 30 }), a("abl.ultimate_orbital", { mult: 2.2 })],
    ["Kastor bricht Linien, indem er zuerst die Mauer einreißt. Seine Doktrin passt in einen Satz: Verteidiger zuerst.", "Kastor breaks lines by tearing down the wall first. His doctrine fits in one sentence: defenders first."]),
  unit("unit.sable_marrow", ["Sable Marrow", "Sable Marrow"], "RDL", "mecha", "commander", "A",
    a("abl.command_pulse", { mult: 0.9, rule: rule("lowest_hp_pct") }, ["Rostimpuls", "Rust Pulse"]),
    [a("abl.pulse_focus", { pct: 18, ticks: 30 }), a("abl.pulse_inspire", { amount: 22 }), a("abl.ultimate_lockdown", { ticks: 15 })],
    ["Ein Redline-Schrottkommandant, der Schwäche riecht. Wer blutet, stirbt zuerst.", "A Redline scrap commander who smells weakness. Whoever bleeds dies first."]),
  unit("unit.ironclad_boone", ["Ironclad Boone", "Ironclad Boone"], "HEX", "tank", "commander", "B",
    a("abl.command_pulse", { mult: 0.8, rule: rule("front") }, ["Feldimpuls", "Field Pulse"]),
    [a("abl.pulse_rally", { pct: 15, ticks: 30 }), a("abl.last_stand", { pct: 18 })],
    ["Boone hat drei Enklaven gehalten und keine aufgegeben. Kein Genie, aber ein Fels.", "Boone held three enclaves and gave up none. No genius, but a rock."]),
  unit("unit.halcyon_vex", ["Halcyon Vex", "Halcyon Vex"], "KRG", "mecha", "commander", "B",
    a("abl.command_pulse", { mult: 0.8, rule: rule("saboteur_first") }, ["Klingenimpuls", "Blade Pulse"]),
    [a("abl.pulse_overdrive", { pct: 15, ticks: 30 }), a("abl.pulse_focus", { pct: 15, ticks: 30 })],
    ["Ein Kurogane-Duellant, der Attentäter jagt, bevor sie zuschlagen.", "A Kurogane duellist who hunts assassins before they strike."]),
  unit("unit.sgt_rook", ["Sgt. Rook", "Sgt. Rook"], "HEX", "tank", "commander", "C",
    a("abl.command_pulse", { mult: 0.8, rule: rule("front") }, ["Befehlsimpuls", "Order Pulse"]),
    [a("abl.pulse_fortify", { pct: 12, ticks: 25 })],
    ["Der Ausbilder, den jeder Rekrut bekommt. Laut, verlässlich, unsterblich langweilig.", "The drill sergeant every recruit gets. Loud, dependable, immortally boring."]),
  unit("unit.cipher_odd", ["Cipher Odd", "Cipher Odd"], "OMC", "android", "commander", "C",
    a("abl.command_pulse", { mult: 0.8, rule: rule("random") }, ["Statikimpuls", "Static Pulse"]),
    [a("abl.pulse_inspire", { amount: 18 })],
    ["Ein OMC-Android, dessen Zielwahl niemand vorhersagen kann, nicht einmal er selbst.", "An OMC android whose targeting nobody can predict, not even itself."]),
  unit("unit.cadet_01", ["Kadett Einheit-01", "Cadet Unit-01"], "HEX", "tank", "commander", "D",
    a("abl.command_pulse", { mult: 0.7, rule: rule("front") }, ["Basisimpuls", "Basic Pulse"]),
    [],
    ["Der Startkommandant jeder Enklave. Hält die Linie, bis Besseres fabriziert ist.", "Every enclave's starter commander. Holds the line until something better is fabricated."]),

  // ─── Defenders ───
  unit("unit.bulwark_titan", ["Bollwerk-Titan", "Bulwark Titan"], "HEX", "tank", "defender", "S",
    a("abl.single_strike", { mult: 1.0 }, ["Belagerungskanone", "Siege Cannon"]),
    [a("abl.taunt", { ticks: 40 }), a("abl.shield_self", { pct: 35, ticks: 60 }), a("abl.bulwark", { cap: 15 }), a("abl.fortify", { pct: 35, ticks: 60 })],
    ["Vierzig Tonnen Wolfram und ein Motor, der Erdbeben verursacht. Nichts kommt an ihm vorbei.", "Forty tons of tungsten and an engine that causes earthquakes. Nothing gets past it."]),
  unit("unit.kagemori_ward", ["Kagemori-Wächter", "Kagemori Ward"], "KRG", "mecha", "defender", "S",
    a("abl.single_strike", { mult: 1.0 }, ["Wächterklinge", "Ward Blade"]),
    [a("abl.shield_ally", { pct: 28, ticks: 60 }), a("abl.taunt", { ticks: 35 }), a("abl.fortify", { pct: 30, ticks: 60 }), a("abl.reflect", { pct: 18 })],
    ["Ein Kurogane-Leibwächter, der andere zuerst schützt und dann alle Angreifer zugleich trifft.", "A Kurogane bodyguard that shields others first and then hits every attacker at once."]),
  unit("unit.rampart_7", ["Rampart-7", "Rampart-7"], "HEX", "tank", "defender", "A",
    a("abl.single_strike", { mult: 1.0 }, ["Schwere Kanone", "Heavy Cannon"]),
    [a("abl.taunt", { ticks: 35 }), a("abl.shield_self", { pct: 28, ticks: 50 }), a("abl.bulwark", { cap: 20 })],
    ["Die siebte Generation der Rampart-Reihe. Die ersten sechs stehen noch.", "Seventh generation of the Rampart line. The first six are still standing."]),
  unit("unit.basalt_golem", ["Basaltgolem", "Basalt Golem"], "RDL", "mecha", "defender", "A",
    a("abl.single_strike", { mult: 1.0 }, ["Schrottfaust", "Scrap Fist"]),
    [a("abl.shield_self", { pct: 25, ticks: 50 }), a("abl.corrode", { pctPerSec: 4, ticks: 50 }), a("abl.taunt", { ticks: 30 })],
    ["Aus Betonbrocken und Bahngleisen gebaut, mit Säure in den Gelenken.", "Built from concrete chunks and railway tracks, with acid in the joints."]),
  unit("unit.shieldwall_mk2", ["Schildwall Mk2", "Shieldwall Mk2"], "HEX", "tank", "defender", "B",
    a("abl.single_strike", { mult: 0.95 }, ["Autokanone", "Autocannon"]),
    [a("abl.taunt", { ticks: 30 }), a("abl.shield_ally", { pct: 20, ticks: 50 })],
    ["Das Arbeitstier jeder Frontlinie. Mark 2 hat endlich eine Klimaanlage.", "The workhorse of every front line. Mark 2 finally has air conditioning."]),
  unit("unit.tessen_guard", ["Tessen-Garde", "Tessen Guard"], "KRG", "mecha", "defender", "B",
    a("abl.single_strike", { mult: 0.95 }, ["Fächerklinge", "Fan Blade"]),
    [a("abl.shield_self", { pct: 22, ticks: 50 }), a("abl.taunt", { ticks: 30 })],
    ["Eine Klinge wie ein Kriegsfächer, ein Schild wie eine Wand.", "A blade like a war fan, a shield like a wall."]),
  unit("unit.barricade", ["Barrikade", "Barricade"], "HEX", "tank", "defender", "C",
    a("abl.single_strike", { mult: 0.9 }, ["Autokanone", "Autocannon"]),
    [a("abl.taunt", { ticks: 25 })],
    ["Billig, schwer, überall. Die Barrikade ist die Antwort auf die meisten Fragen.", "Cheap, heavy, everywhere. The Barricade answers most questions."]),
  unit("unit.scrap_bastion", ["Schrottbastion", "Scrap Bastion"], "RDL", "tank", "defender", "C",
    a("abl.single_strike", { mult: 0.9 }, ["Schrottkanone", "Junk Cannon"]),
    [a("abl.shield_self", { pct: 18, ticks: 40 })],
    ["Ein Panzer aus Autotüren. Die Säure ist Absicht.", "A tank made of car doors. The acid is intentional."]),
  unit("unit.pillbox", ["Pillbox", "Pillbox"], "HEX", "tank", "defender", "D",
    a("abl.single_strike", { mult: 0.85 }, ["Maschinengewehr", "Machine Gun"]),
    [],
    ["Ein Bunker mit Ketten. Bewegt sich langsam, fällt langsam.", "A bunker on tracks. Moves slowly, falls slowly."]),

  // ─── Attackers ───
  unit("unit.hellion_vanguard", ["Hellion-Vorhut", "Hellion Vanguard"], "KRG", "mecha", "attacker", "S",
    a("abl.single_strike", { mult: 1.15 }, ["Plasmaklinge", "Plasma Blade"]),
    [a("abl.multi_strike", { targets: 3, mult: 0.8 }), a("abl.armor_break", { pct: 30, ticks: 40 }), a("abl.barrage", { hits: 5, mult: 0.45 }), a("abl.overload", { mult: 3.0, selfPct: 8 })],
    ["Kuroganes Vorzeigekrieger: zwei Plasmaklingen, keine Bremse.", "Kurogane's showpiece warrior: two plasma blades, no brakes."]),
  unit("unit.solaris_lance", ["Solaris-Lanze", "Solaris Lance"], "VLK", "tripod", "attacker", "S",
    a("abl.single_strike", { mult: 1.15 }, ["Solarstrahl", "Solar Beam"]),
    [a("abl.pierce", { pct: 40, mult: 1.4 }), a("abl.chain", { bounces: 3, decay: 75, mult: 1.1 }), a("abl.mark", { pct: 25, ticks: 45 }), a("abl.execute", { threshold: 30, bonus: 130 })],
    ["Ein Dreibein, das Sonnenlicht bündelt, bis Stahl weint.", "A tripod that focuses sunlight until steel weeps."]),
  unit("unit.ryujin_blade", ["Ryujin-Klinge", "Ryujin Blade"], "KRG", "mecha", "attacker", "A",
    a("abl.single_strike", { mult: 1.1 }, ["Zwillingsklingen", "Twin Blades"]),
    [a("abl.multi_strike", { targets: 2, mult: 0.8 }), a("abl.armor_break", { pct: 25, ticks: 35 }), a("abl.barrage", { hits: 4, mult: 0.42 })],
    ["Schnell wie ein Drache aus alten Geschichten, laut wie ein Turbinenfeuer.", "Fast as a dragon from old stories, loud as a turbine fire."]),
  unit("unit.photon_stalker", ["Photonenjäger", "Photon Stalker"], "VLK", "tripod", "attacker", "A",
    a("abl.single_strike", { mult: 1.1 }, ["Photonenstrahl", "Photon Beam"]),
    [a("abl.pierce", { pct: 30, mult: 1.3 }), a("abl.chain", { bounces: 2, decay: 70, mult: 1.0 }), a("abl.execute", { threshold: 25, bonus: 110 })],
    ["Ein Jäger, der markiert, verfolgt und beendet.", "A hunter that marks, tracks and finishes."]),
  unit("unit.striker_beta", ["Striker Beta", "Striker Beta"], "KRG", "mecha", "attacker", "B",
    a("abl.single_strike", { mult: 1.05 }, ["Pulsgewehr", "Pulse Rifle"]),
    [a("abl.multi_strike", { targets: 2, mult: 0.7 }), a("abl.barrage", { hits: 3, mult: 0.4 })],
    ["Der Standard-Mecha der Kurogane-Milizen. Beta, weil Alpha nie fertig wurde.", "The standard mecha of the Kurogane militias. Beta because Alpha was never finished."]),
  unit("unit.argent_tripod", ["Silber-Dreibein", "Argent Tripod"], "VLK", "tripod", "attacker", "B",
    a("abl.single_strike", { mult: 1.05 }, ["Bogenstrahl", "Arc Beam"]),
    [a("abl.pierce", { pct: 25, mult: 1.2 }), a("abl.chain", { bounces: 2, decay: 65, mult: 0.95 })],
    ["Silbern, spindeldürr, tödlich präzise.", "Silver, spindly, lethally precise."]),
  unit("unit.grunt_cannon", ["Grunt-Kanone", "Grunt Cannon"], "HEX", "tank", "attacker", "C",
    a("abl.single_strike", { mult: 1.0 }, ["Haubitze", "Howitzer"]),
    [a("abl.aoe_blast", { mult: 0.55 })],
    ["Eine Haubitze auf Ketten. Zielt grob, trifft breit.", "A howitzer on tracks. Aims roughly, hits broadly."]),
  unit("unit.fang", ["Fang", "Fang"], "RDL", "mecha", "attacker", "C",
    a("abl.single_strike", { mult: 1.0 }, ["Kettensäge", "Chain Saw"]),
    [a("abl.barrage", { hits: 3, mult: 0.38 })],
    ["Eine Kettensäge mit Beinen. Redline nennt das Design.", "A chainsaw with legs. Redline calls that design."]),
  unit("unit.rustbucket", ["Rosteimer", "Rustbucket"], "RDL", "tank", "attacker", "D",
    a("abl.single_strike", { mult: 0.95 }, ["Schrottgewehr", "Scrap Gun"]),
    [],
    ["Es fährt. Es schießt. Manchmal beides gleichzeitig.", "It drives. It shoots. Sometimes both at once."]),

  // ─── Saboteurs ───
  unit("unit.widow_zero", ["Witwe Null", "Widow Zero"], "RDL", "mecha", "saboteur", "S",
    a("abl.single_strike", { mult: 1.2 }, ["Nadelgewehr", "Needle Gun"]),
    [a("abl.snipe_rear", { mult: 2.0 }), a("abl.emp", { drain: 50 }), a("abl.execute", { threshold: 35, bonus: 140 }), a("abl.overload", { mult: 3.2, selfPct: 12 })],
    ["Sie schlägt dort zu, wo kein Schild ist: beim Commander.", "She strikes where there is no shield: at the commander."]),
  unit("unit.nightshade", ["Nachtschatten", "Nightshade"], "VLK", "tripod", "saboteur", "S",
    a("abl.single_strike", { mult: 1.2 }, ["Phasenlanze", "Phase Lance"]),
    [a("abl.snipe_rear", { mult: 1.9 }), a("abl.blind", { pct: 35, ticks: 40 }), a("abl.mark", { pct: 25, ticks: 45 }), a("abl.shock", { ticks: 9, mult: 1.0 })],
    ["Ein Dreibein, das zwischen Phasen springt und Sensoren blendet.", "A tripod that jumps between phases and blinds sensors."]),
  unit("unit.ember_wraith", ["Glutgeist", "Ember Wraith"], "RDL", "mecha", "saboteur", "A",
    a("abl.single_strike", { mult: 1.15 }, ["Flammenpike", "Flame Pike"]),
    [a("abl.snipe_rear", { mult: 1.7 }), a("abl.corrode", { pctPerSec: 5, ticks: 50 }), a("abl.overload", { mult: 2.6, selfPct: 10 })],
    ["Brennt, was sie berührt, und berührt nur die Wichtigen.", "Burns what it touches and touches only the important ones."]),
  unit("unit.kitsune_9", ["Kitsune-9", "Kitsune-9"], "KRG", "mecha", "saboteur", "A",
    a("abl.single_strike", { mult: 1.15 }, ["Vibroklauen", "Vibro Claws"]),
    [a("abl.snipe_rear", { mult: 1.6 }), a("abl.blind", { pct: 30, ticks: 35 }), a("abl.armor_break", { pct: 30, ticks: 35 })],
    ["Neun Schwänze aus Sensorkabel, Klauen aus Vibrostahl.", "Nine tails of sensor cable, claws of vibro-steel."]),
  unit("unit.spark_plug", ["Zündkerze", "Spark Plug"], "RDL", "tank", "saboteur", "B",
    a("abl.single_strike", { mult: 1.1 }, ["Teslabogen", "Tesla Arc"]),
    [a("abl.emp", { drain: 35 }), a("abl.snipe_rear", { mult: 1.4 })],
    ["Ein Kleinpanzer mit einer Teslaspule statt Kanone.", "A mini tank with a Tesla coil instead of a cannon."]),
  unit("unit.mirage", ["Fata Morgana", "Mirage"], "VLK", "tripod", "saboteur", "B",
    a("abl.single_strike", { mult: 1.1 }, ["Spiegelstrahl", "Mirror Beam"]),
    [a("abl.blind", { pct: 25, ticks: 30 }), a("abl.snipe_rear", { mult: 1.4 })],
    ["Ist da, ist weg, hat schon getroffen.", "Is there, is gone, has already hit."]),
  unit("unit.firecracker", ["Knallfrosch", "Firecracker"], "RDL", "mecha", "saboteur", "C",
    a("abl.single_strike", { mult: 1.05 }, ["Bombenwurf", "Bomb Toss"]),
    [a("abl.aoe_blast", { mult: 0.6 })],
    ["Wirft, was explodiert. Fragt nicht, was.", "Throws what explodes. Never asks what."]),
  unit("unit.glitch", ["Glitch", "Glitch"], "OMC", "android", "saboteur", "C",
    a("abl.single_strike", { mult: 1.05 }, ["Datenspitze", "Data Spike"]),
    [a("abl.emp", { drain: 30 })],
    ["Ein OMC-Android, der Gegnersysteme mit schlechtem Code füttert.", "An OMC android that feeds enemy systems bad code."]),
  unit("unit.nailgun", ["Nagler", "Nailgun"], "RDL", "mecha", "saboteur", "D",
    a("abl.single_strike", { mult: 1.0 }, ["Nagelpistole", "Nail Gun"]),
    [],
    ["Ein Baustellenwerkzeug, das Karriere gemacht hat.", "A construction tool that made a career."]),

  // ─── Supports ───
  unit("unit.seraph_unit", ["Seraph-Einheit", "Seraph Unit"], "OMC", "android", "support", "S",
    a("abl.single_strike", { mult: 0.8 }, ["Halostrahl", "Halo Ray"]),
    [aura("atk", 12), a("abl.repair_pulse", { pct: 15 }), a("abl.reboot", { pct: 45 }), a("abl.energize", { amount: 30 })],
    ["Der Engel des Kollektivs: repariert, belebt, reinigt.", "The Collective's angel: repairs, revives, cleanses."]),
  unit("unit.halo_mother", ["Halo-Mutter", "Halo Mother"], "OMC", "drone_carrier", "support", "S",
    a("abl.single_strike", { mult: 0.8 }, ["Drohnenschwarm", "Drone Swarm"]),
    [aura("def", 12), a("abl.repair_all", { pct: 12 }), a("abl.shield_ally", { pct: 25, ticks: 60 }), aura("energy", 15)],
    ["Ein Träger, der seine Drohnen wie Kinder aussendet und wie Schilde zurückholt.", "A carrier that sends its drones out like children and calls them back like shields."]),
  unit("unit.medic_prime", ["Medic Prime", "Medic Prime"], "OMC", "android", "support", "A",
    a("abl.single_strike", { mult: 0.75 }, ["Medstrahl", "Med Beam"]),
    [aura("hp", 10), a("abl.repair_pulse", { pct: 13 }), a("abl.cleanse", { count: 2 })],
    ["Der Feldarzt für Maschinen. Schweißt schneller, als Gegner schneiden.", "The field medic for machines. Welds faster than enemies cut."]),
  unit("unit.aurora_node", ["Aurora-Knoten", "Aurora Node"], "VLK", "drone_carrier", "support", "A",
    a("abl.single_strike", { mult: 0.75 }, ["Aurorafeld", "Aurora Field"]),
    [aura("crit", 8), a("abl.overcharge", { pct: 25, ticks: 50 }), a("abl.shield_ally", { pct: 20, ticks: 50 })],
    ["Ein Valkyr-Knoten, der Zielrechner überlädt und Schilde spinnt.", "A Valkyr node that overclocks targeting cores and spins shields."]),
  unit("unit.patch_bot", ["Patch-Bot", "Patch-Bot"], "OMC", "android", "support", "B",
    a("abl.single_strike", { mult: 0.7 }, ["Schweißgerät", "Welder"]),
    [aura("hp", 6), a("abl.repair_pulse", { pct: 10 })],
    ["Klein, fleißig, unverzichtbar.", "Small, diligent, indispensable."]),
  unit("unit.relay_kite", ["Relais-Drachen", "Relay Kite"], "KRG", "drone_carrier", "support", "B",
    a("abl.single_strike", { mult: 0.7 }, ["Relais-Ping", "Relay Ping"]),
    [aura("atk", 6), a("abl.energize", { amount: 18 })],
    ["Ein Drachen aus Antennen, der den Trupp taktet.", "A kite of antennas that keeps the squad in tempo."]),
  unit("unit.field_nurse", ["Feldschwester", "Field Nurse"], "OMC", "android", "support", "C",
    a("abl.single_strike", { mult: 0.65 }, ["Nanospray", "Nano Spray"]),
    [aura("hp", 4)],
    ["Sprüht Nanite, flucht auf Latein.", "Sprays nanites, swears in Latin."]),
  unit("unit.battery_mule", ["Batterie-Maultier", "Battery Mule"], "HEX", "drone_carrier", "support", "C",
    a("abl.single_strike", { mult: 0.65 }, ["Stromzufuhr", "Power Feed"]),
    [aura("energy", 8)],
    ["Ein Akku auf Rädern, der andere schneller schießen lässt.", "A battery on wheels that makes others shoot faster."]),
  unit("unit.toolbox", ["Werkzeugkiste", "Toolbox"], "OMC", "android", "support", "D",
    aura("def", 3),
    [],
    ["Buchstäblich eine Werkzeugkiste mit Beinen.", "Literally a toolbox with legs."]),
];

const unitMap = new Map(UNITS.map((u) => [u.id, u]));

export function unitById(id: string): UnitDef {
  const u = unitMap.get(id);
  if (!u) throw new Error(`unknown unit ${id}`);
  return u;
}

export function hasUnit(id: string): boolean {
  return unitMap.has(id);
}

/** The starter roster granted to every new account. */
export const STARTER_UNITS: readonly string[] = ["unit.cadet_01", "unit.pillbox", "unit.rustbucket", "unit.toolbox"];
